import { useCallback, useEffect, useState } from 'react';

import type { Event, Layout } from '@rive-app/react-canvas';
import { EventType, useRive, useStateMachineInput } from '@rive-app/react-canvas';

import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import { useLoadingTimeout } from '../useLoadingTimeout';

import { RIVE_DEFAULTS } from './constants';

interface UseRiveMachineProps {
  src?: string;
  stateMachineName?: string;
  autoplay?: boolean;
  autoBind?: boolean;
  layout?: Layout;
  artboard?: string;
  onLoad?: () => void;
  onLoadError?: (error: any) => void;
  loadingTimeout?: number;
  onStateChange?: (event: Event) => void;
  onRiveEvent?: (event: Event) => void;
}

export const useRiveMachine = ({
  src,
  stateMachineName = RIVE_DEFAULTS.STATE_MACHINE_NAME,
  autoplay = true,
  autoBind = false,
  artboard,
  onLoad,
  onLoadError,
  loadingTimeout = RIVE_DEFAULTS.TIMEOUT,
  layout = RIVE_DEFAULTS.LAYOUT,
  onStateChange,
  onRiveEvent,
}: UseRiveMachineProps) => {
  const { riveFileStore } = useStores();
  const [error, setError] = useState<Event | null>(null);
  const [hasRiveLoaded, setHasRiveLoaded] = useState<boolean>(false);

  const handleLoad = useCallback(() => {
    setHasRiveLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleLoadError = useCallback(
    (loadError: any) => {
      onLoadError?.(loadError);
      setError(loadError);
      logToNewRelic('Rive Load Error', { message: loadError, src }, 'error');
    },
    [onLoadError, src],
  );

  const riveFile = riveFileStore.getFile(src);
  const { rive, RiveComponent } = useRive({
    src,
    riveFile,
    stateMachines: stateMachineName,
    artboard,
    autoplay,
    autoBind,
    layout,
    onLoad: handleLoad,
    onLoadError: handleLoadError,
    onStateChange,
  });

  useEffect(() => {
    if (!rive || !onRiveEvent) return;

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
    };
  }, [rive, onRiveEvent]);

  useEffect(() => {
    setHasRiveLoaded(false);
    setError(null);
  }, [src]);

  const isLoaded = hasRiveLoaded || Boolean(riveFile) || Boolean(rive?.isPlaying);

  const { isLoading: isTimeoutLoading, error: timeoutError } = useLoadingTimeout({
    resource: src,
    hasLoaded: isLoaded,
    timeoutEnabled: Boolean(src) && !riveFile,
    timeout: loadingTimeout,
    onTimeout: onLoadError,
  });

  const fireRiveViewModelTrigger = (triggerPath: string): boolean => {
    if (!rive) return false;

    const vm = rive.viewModelInstance;

    if (!vm) return false;

    const trigger = vm.trigger?.(triggerPath);

    if (!trigger) return false;

    if (typeof trigger.trigger === 'function') {
      trigger.trigger();
      return true;
    } else {
      logToNewRelic('Rive View Model Trigger', { message: 'Trigger not found', triggerPath }, 'error');
    }

    return false;
  };

  const setRiveViewModelEnum = (enumPath: string, value: string): boolean => {
    if (!rive) return false;

    const vm = rive.viewModelInstance;
    if (!vm) return false;

    const enumProp = vm.enum?.(enumPath);
    if (!enumProp) return false;

    enumProp.value = value;
    return true;
  };

  return {
    rive,
    isLoading: isTimeoutLoading && !riveFile,
    timeoutError,
    error,
    RiveComponent,
    useStateMachineInput,
    fireRiveViewModelTrigger,
    setRiveViewModelEnum,
  };
};
